<script lang="ts">
export interface ToolbarProps<TData extends RowData> {
	options: FacetedOptions<TData>;
	fieldFilters?: ColumnFiltersState;
	maxCount?: number;
	countBadge?: boolean;
}
</script>

<script setup lang="ts" generic="T extends RowData">
import { mdiTune } from "@mdi/js";
import type { ColumnFiltersState, RowData } from "@tanstack/vue-table";
import { computed } from "vue";
import { useScreenSize } from "../../composables/screen-size";
import { useLocale } from "../../locale";
import Button from "../button/Button.vue";
import { injectDataCollectionContext } from "../data-collection/composables/data-collection";
import Icon from "../icon/Icon.vue";
import Popover from "../popover/Popover.vue";
import { useDataCollectionStateModel } from "./composables/data-collection-state-model";
import type { FacetedOptions } from "./composables/faceted-types";
import { useFilterableColumns } from "./composables/filterable-columns";
import DataCollectionFacetedFilterMenuItem from "./DataCollectionFacetedFilterMenuItem.vue";

const props = defineProps<ToolbarProps<T>>();
const emit = defineEmits<{
	"update:fieldFilters": [val: ColumnFiltersState];
}>();

defineOptions({ inheritAttrs: false });

const { t } = useLocale();

const { state } = injectDataCollectionContext();

useDataCollectionStateModel("fieldFilters", props, emit, state);

const { sortedFilterableColumns, activeFiltersCount, clearAllFilters } = useFilterableColumns(
	() => props.options
);

const hasActiveFilter = computed(() => activeFiltersCount.value > 0);

const { mdOrLower } = useScreenSize();
</script>

<template>
	<Popover v-if="sortedFilterableColumns.length > 0" :align="mdOrLower ? 'end' : 'start'">
		<template #trigger>
			<div class="indicator" v-bind="$attrs">
				<span
					v-if="hasActiveFilter && countBadge"
					class="indicator-item badge badge-primary badge-xs"
				>
					{{ activeFiltersCount }}
				</span>
				<Button
					class="border-border"
					:variant="hasActiveFilter ? 'soft' : 'outline'"
					:color="hasActiveFilter ? 'primary' : 'default'"
				>
					<Icon :d="mdiTune" />
					<span class="hidden md:block">
						{{ t("table.filterPlural") }}
					</span>
				</Button>
			</div>
		</template>

		<div class="flex min-w-64 flex-col gap-2 p-2">
			<DataCollectionFacetedFilterMenuItem
				v-for="col of sortedFilterableColumns"
				:key="col.id"
				:options="props.options?.[col.id]"
				:maxCount="maxCount"
				:column="col as any"
			/>

			<Button v-if="activeFiltersCount > 0" class="mt-2" variant="soft" @click="clearAllFilters">
				{{ t("table.clear-filters") }}
			</Button>
		</div>
	</Popover>
</template>
